"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.QuantityPicker = void 0;
var _react = require("@floating-ui/react");
var _icons = require("@ori-ui/icons");
var _material = require("@ori-ui/mui-bundle/material");
var _clsx = _interopRequireDefault(require("clsx"));
var _react2 = require("react");
var _utils = require("../utils");
var _constants = require("./constants");
var _quantityPickerStyled = require("./quantityPickerStyled");
var _useDebug = require("./useDebug");
var _useQuantityPicker = require("./useQuantityPicker");
var _useVisualProps = require("./useVisualProps");
var _jsxRuntime = require("react/jsx-runtime");
const QuantityPicker = exports.QuantityPicker = /*#__PURE__*/(0, _react2.forwardRef)((inProps, ref) => {
  const props = (0, _material.useThemeProps)({
    props: inProps,
    name: 'MuiOriQuantityPicker'
  });
  const {
    className,
    component = 'div',
    DecreaseIconButtonProps = {},
    disabled = false,
    expanded,
    expandOnFocus = false,
    height,
    IncreaseIconButtonProps = {},
    InputProps: InputBaseProps = {},
    labelsTranslations = {},
    onChange,
    onClickAway,
    onFocus,
    minValue = _constants.DEFAULT_MIN_VALUE,
    readOnly = false,
    required,
    size = 'medium',
    tooltipMaxText,
    variant = 'contained',
    width,
    maxValue = _constants.DEFAULT_MAX_VALUE,
    maxGroupValue = maxValue,
    tooltipMaxDisabled = false,
    ...otherProps
  } = props;
  const [focused, setFocused] = (0, _react2.useState)(expanded ?? false);
  const {
    groupValue,
    handleBlur,
    handleChange,
    handleKeyDown,
    id,
    InputProps,
    lastIncreased,
    logBase,
    onDecrease,
    onIncrease,
    value
  } = (0, _useQuantityPicker.useQuantityPicker)(props);
  const {
    log
  } = (0, _useDebug.useDebug)(`ID-${id}`);
  const {
    cssProps,
    buttonProps
  } = (0, _useVisualProps.useVisualProps)(props);
  const ownerState = {
    ...props,
    cssProps,
    disabled,
    expandOnFocus,
    focused,
    readOnly,
    size,
    variant
  };
  (0, _react2.useEffect)(() => {
    if (typeof expanded === 'boolean') {
      setFocused(expanded);
    }
  }, [expanded]);
  const handleClickAway = (0, _react2.useCallback)(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(false);
  }, [expanded]);
  const handleFocus = (0, _react2.useCallback)(() => {
    if (typeof expanded === 'boolean') {
      return;
    }
    setFocused(true);
  }, [expanded]);
  const handleFocusForked = (0, _utils.useForkCallback)(handleFocus, onFocus);
  const handleClickAwayForked = (0, _utils.useForkCallback)(handleClickAway, onClickAway);
  const classes = (0, _quantityPickerStyled.useUtilityClasses)(ownerState);
  const inputRef = (0, _react2.useRef)(null);
  const [tooltipMaxOpen, setTooltipMaxOpen] = (0, _react2.useState)(false);
  const arrowRef = (0, _react2.useRef)(null);
  const {
    refs: {
      setReference,
      setFloating
    },
    floatingStyles,
    context
  } = (0, _react.useFloating)({
    open: tooltipMaxOpen,
    onOpenChange: setTooltipMaxOpen,
    placement: 'top',
    strategy: 'fixed',
    middleware: [(0, _react.offset)(15), (0, _react.shift)(), (0, _react.flip)(), (0, _react.arrow)({
      element: arrowRef
    })],
    whileElementsMounted: _react.autoUpdate
  });
  const maxReached = (0, _react2.useMemo)(() => {
    const max = Math.min(maxValue, maxGroupValue);
    return value >= max || groupValue >= maxGroupValue;
  }, [groupValue, maxGroupValue, maxValue, value]);
  const dismiss = (0, _react.useDismiss)(context);
  const role = (0, _react.useRole)(context, {
    role: 'alertdialog'
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = (0, _react.useInteractions)([dismiss, role]);
  const {
    styles: transitionStyles
  } = (0, _react.useTransitionStyles)(context, {
    initial: {
      opacity: 0,
      transform: 'scale(0.8)'
    }
  });
  const isTooltipMaxUsable = (0, _react2.useMemo)(() => {
    if (!tooltipMaxDisabled && typeof tooltipMaxText === 'string' && Boolean(tooltipMaxText) && maxValue < _constants.DEFAULT_MAX_VALUE) {
      return true;
    }
    return false;
  }, [maxValue, tooltipMaxDisabled, tooltipMaxText]);
  (0, _react2.useEffect)(() => {
    setTooltipMaxOpen(maxReached && lastIncreased === id);
  }, [id, lastIncreased, maxReached]);
  const showTooltip = (0, _react2.useCallback)(() => {
    setTooltipMaxOpen(true);
  }, []);
  const increaseWrapper = (0, _react2.useMemo)(() => {
    const wrapperAttributes = {
      tabIndex: -1
    };
    if (isTooltipMaxUsable) {
      wrapperAttributes.ref = setReference;
      const referenceProps = getReferenceProps();
      for (const key in referenceProps) {
        if (Object.hasOwn(referenceProps, key)) {
          const propKey = key;
          wrapperAttributes[propKey] = referenceProps[key];
        }
      }
    }
    if (isTooltipMaxUsable && maxReached) {
      wrapperAttributes.role = 'button';
      wrapperAttributes.tabIndex = 0;
      wrapperAttributes.onClick = showTooltip;
      wrapperAttributes.onKeyDown = showTooltip;
    }
    return wrapperAttributes;
  }, [getReferenceProps, isTooltipMaxUsable, maxReached, setReference, showTooltip]);
  const logMax = (0, _react2.useMemo)(() => {
    const newLine = '\r\n ';
    const values = `value / maxValue:  ${value} / ${maxValue}`;
    const groups = `➖ groupValue / maxGroupValue:  ${groupValue} / ${maxGroupValue}`;
    const max = maxReached ? '🔝MAX IS REACHED' : '';
    const tooltip = isTooltipMaxUsable ? tooltipMaxOpen ? ' 🪧 tooltip is opened' : '' : ' ⚠️ tooltip is not usable';
    return [newLine, values, groups, newLine, max, tooltip];
  }, [groupValue, isTooltipMaxUsable, maxGroupValue, maxReached, maxValue, tooltipMaxOpen, value]);
  log(...logBase, ...logMax);

  // preparing input props
  const {
    inputProps,
    ...otherInputProps
  } = InputProps;
  const inputElementProps = (0, _react2.useMemo)(() => ({
    'aria-label': labelsTranslations?.inputLabel ?? 'Quantity',
    ...inputProps
  }), [inputProps, labelsTranslations.inputLabel]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_material.ClickAwayListener, {
    onClickAway: handleClickAwayForked,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_quantityPickerStyled.QuantityPickerRoot, {
      ...otherProps,
      ref: ref,
      ownerState: ownerState,
      className: (0, _clsx.default)(classes.root, className),
      as: component,
      onFocus: handleFocusForked,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.IconButtonRoot, {
        disableElevation: true,
        ownerState: ownerState,
        disabled: disabled || value <= minValue,
        className: (0, _clsx.default)(classes.iconButton, classes.decreaseButton),
        "aria-label": labelsTranslations?.decreaseButtonLabel ?? 'Decrease quantity',
        ...buttonProps,
        ...DecreaseIconButtonProps,
        onClick: onDecrease,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Remove, {
          fontSize: "inherit"
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.InputRoot, {
        inputRef: inputRef,
        ownerState: ownerState,
        readOnly: readOnly,
        required: required,
        value: value,
        disabled: disabled,
        className: classes.input,
        ...InputBaseProps,
        ...otherInputProps,
        inputProps: inputElementProps,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.IncreaseButtonWrapper, {
        ...increaseWrapper,
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.IconButtonRoot, {
          disableElevation: true,
          ownerState: ownerState,
          disabled: disabled || maxReached,
          className: (0, _clsx.default)(classes.iconButton, classes.increaseButton),
          "aria-label": labelsTranslations?.increaseButtonLabel ?? 'Increase quantity',
          ...buttonProps,
          ...IncreaseIconButtonProps,
          onClick: onIncrease,
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Add, {
            fontSize: "inherit"
          })
        })
      }), isTooltipMaxUsable && tooltipMaxOpen ? /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        ref: setFloating,
        style: {
          ...floatingStyles,
          zIndex: 1
        },
        ...getFloatingProps(),
        children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_quantityPickerStyled.TooltipContainer, {
          style: transitionStyles,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_quantityPickerStyled.TooltipArrow, {
            ref: arrowRef,
            tipRadius: 2,
            height: 8,
            context: context
          }), tooltipMaxText]
        })
      }) : null]
    })
  });
});
var _default = exports.default = QuantityPicker;
